import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { LinkedIn } from './icons';
import styles from './Contact.module.css';

const INTERESTS = [
  'NetSuite Consulting',
  'Custom Development',
  'Integration',
  'Training & Mentoring',
  'AI Enablement & Audit',
  'New Product / SaaS',
];

const FEATURES = [
  {
    title: 'NetSuite Consulting',
    sub: 'Since 2007 · 150+ projects delivered.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  },
  {
    title: 'Full-Stack Development',
    sub: '5 SaaS products built and in production.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
      </svg>
    ),
  },
  {
    title: 'AI Enablement & Audits',
    sub: "Train your team on NetSuite's native AI.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      </svg>
    ),
  },
];

export default function Contact() {
  const formRef = useRef(null);
  const [values, setValues] = useState({
    name: '',
    email: '',
    interest: INTERESTS[0],
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const update = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    setSending(true);
    setError(false);

    const templateParams = {
      name: values.name,
      email: values.email,
      type: values.interest,
      message: values.message,
      toname: 'JPCW',
    };

    emailjs
      .send('jpcw_contact', 'jpcw_contact', templateParams, 'olK3_v7TseAvHUtzG')
      .then(() => setSent(true))
      .catch((err) => {
        console.error('Contact form send failed', err);
        setError(true);
      })
      .finally(() => setSending(false));
  };

  return (
    <section className="section" id="contact">
      <div className="wrap">
        <div className={`${styles.contactWrap} reveal`}>
          <div className={styles.contactLeft}>
            <span className="eyebrow">Contact</span>
            <h2>Let's Build Something Together</h2>
            <p>
              Whether it's optimizing your NetSuite, building AI-powered solutions, or developing a new
              SaaS product — I'm ready to help.
            </p>

            <div className={styles.contactFeat}>
              {FEATURES.map((f) => (
                <div className={styles.row} key={f.title}>
                  <div className={styles.ic}>{f.icon}</div>
                  <div>
                    <b>{f.title}</b>
                    <span>{f.sub}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.contactCal}>
              <a
                href="mailto:sales@jpcustomworks.com?subject=Free%20Consultation%20Request"
                className={`btn btn-primary ${styles.calBtn}`}
              >
                Schedule Free Consultation
              </a>
              <p className={styles.sub}>30 minutes. Your agenda. No sales pitch.</p>
              <a
                className={styles.li}
                href="https://www.linkedin.com/company/jp-custom-works"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedIn />
                Connect on LinkedIn
              </a>
            </div>
          </div>

          <div className={styles.contactRight}>
            {!sent && (
              <form id="contactForm" ref={formRef} onSubmit={handleSubmit} noValidate>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label htmlFor="cf-name">
                      Name <span className={styles.req}>*</span>
                    </label>
                    <input
                      type="text"
                      id="cf-name"
                      name="name"
                      autoComplete="name"
                      required
                      placeholder="Jane Doe"
                      value={values.name}
                      onChange={update('name')}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label htmlFor="cf-email">
                      Email <span className={styles.req}>*</span>
                    </label>
                    <input
                      type="email"
                      id="cf-email"
                      name="email"
                      autoComplete="email"
                      required
                      placeholder="jane@company.com"
                      value={values.email}
                      onChange={update('email')}
                    />
                  </div>
                </div>
                <div className={styles.formField}>
                  <label htmlFor="cf-interest">Area of Interest</label>
                  <select id="cf-interest" name="interest" value={values.interest} onChange={update('interest')}>
                    {INTERESTS.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formField}>
                  <label htmlFor="cf-message">
                    Message <span className={styles.req}>*</span>
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    required
                    placeholder="Tell me a bit about what you're working on…"
                    value={values.message}
                    onChange={update('message')}
                  />
                </div>
                <button type="submit" className={`btn btn-primary ${styles.submitBtn}`} disabled={sending}>
                  {sending ? 'Sending…' : 'Send Message'}
                </button>
                {error && (
                  <p className={styles.formError} role="alert">
                    Something went wrong sending your message. Please email{' '}
                    <a href="mailto:sales@jpcustomworks.com">sales@jpcustomworks.com</a> directly.
                  </p>
                )}
              </form>
            )}

            <div className={`${styles.formSuccess} ${sent ? styles.show : ''}`} role="status" aria-live="polite">
              <div className={styles.check}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M4 12l5 5L20 6" />
                </svg>
              </div>
              <h3>Thanks for reaching out!</h3>
              <p>I'll be in touch soon.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
